import React, { type FC, useEffect, useMemo } from 'react';
import type { TablePaginationConfig, ColumnsType } from 'antd/es/table';
import { Table, Spin, Empty } from 'antd';
import { Link, Space, Text, Button } from '@g2u/ui-kit';
import { PlusOutlined } from '@ant-design/icons';
import { format, isValid, parse } from 'date-fns';

import { encryptAesToken, useMessages, useSearchParamsInUrl } from 'helpers';
import { Condition, type Email, type SMS } from 'types';
import env from 'env';

import styles from './message-list.module.scss';

export namespace MessageList {
  type UseMessages = Parameters<typeof useMessages>;

  export type Props = {
    for: UseMessages[0];
    messageType: UseMessages[1];
    id: string;
    hideColumns?: ColumnKeys[];
    onAdd?: () => void;
    triggerRefetch?: boolean;
  };

  type ColumnKeys = (typeof columnsDefinition)[number]['key'] | 'actions';

  const linkTypeToUrl: Record<string, (id: string | number) => string> = {
    Customer: (id) => `/customer/edit/${id}`,
    Technician: (id) => `${env.LEGACY_BMS_URL}/technicians/index/edit/id/${encryptAesToken(id.toString())}`,
  };

  const columnsDefinition = [
    {
      key: 'sentTime',
      title: 'Sent time',
      render: (_, record) => {
        const parsedDate = parse(
          'sent_datetime' in record ? record.sent_datetime : record.datetime,
          'yyyy-MM-dd HH:mm:ss',
          new Date(),
        );
        return isValid(parsedDate) ? format(parsedDate, 'dd MMM yyyy HH:mm') : '';
      },
      width: 140,
    },
    {
      key: 'createdBy',
      title: 'Created by',
      dataIndex: ['user', 'name'],
      minWidth: 100,
    },
    {
      key: 'sentTo',
      title: 'Sent to',
      render: (_, record) => (
        <Space.$ direction={Space.Direction.Vertical}>
          <span>{record.link_type}</span>

          <span>
            {'number' in record ? (
              record.number
            ) : (
              <Link.$ href={linkTypeToUrl[record.link_type](record.link_ID)}>{record.link_name}</Link.$>
            )}
          </span>
        </Space.$>
      ),
      minWidth: 110,
    },
    {
      key: 'subject',
      title: 'Subject',
      dataIndex: ['subject'],
      minWidth: 150,
    },
    {
      key: 'message',
      title: 'Message',
      dataIndex: ['message'],
      minWidth: 265,
    },
    {
      key: 'status',
      title: 'Status',
      render: (_, record) =>
        'response' in record ? record.response : <div>{record.sent === Condition.Yes ? 'Sent' : 'Pending'}</div>,
      width: 90,
    },
  ] as const satisfies ColumnsType<Email | SMS>;

  const defaultPageSize = 5;

  export const $: FC<Props> = ({
    for: resource, // Rename since for is reserved word
    messageType,
    id,
    onAdd,
    triggerRefetch,
  }) => {
    const [{ page: currentPage, recordsPerPage }, setSearchParams] = useSearchParamsInUrl({
      page: 1,
      recordsPerPage: defaultPageSize,
    });
    const [messages, total = 0, isLoading, refetch] = useMessages(
      resource,
      messageType,
      id,
      currentPage,
      recordsPerPage,
    );

    useEffect(() => {
      triggerRefetch && refetch();
    }, [triggerRefetch]);

    const cleanedMessages = useMemo(
      () => messages?.map((message) => ({ ...message, message: message.message.replaceAll(/(<([^>]+)>)/g, '') })),
      [messages],
    );

    const columns = useMemo(() => {
      return (columnsDefinition as ColumnsType<Email | SMS>).filter(
        ({ key }) => !(key && messageType === 'sms' && key === 'subject'),
      );
    }, [columnsDefinition, messageType]);

    if (!messages) {
      return <Spin data-testid="spinner" className={styles.messageList__spin} />;
    }

    const AddNewButton: FC = () => {
      return onAdd ? (
        <Button.$ type={Button.Type.Primary} onClick={onAdd} dataTestId="add-new" icon={<PlusOutlined />}>
          New <span>{messageType === 'sms' ? 'SMS' : 'Email'}</span>
        </Button.$>
      ) : (
        <> </>
      );
    };

    if (messages && !messages.length) {
      return (
        <Space.$ direction={Space.Direction.Vertical}>
          <br />
          <Empty
            styles={{ image: { height: 60 } }}
            style={{ marginBottom: 20 }}
            description={<Text.$ type="secondary">Sorry, there are no matching results.</Text.$>}
          />
          <div>
            <AddNewButton />
          </div>
        </Space.$>
      );
    }

    const paginationProps: TablePaginationConfig | undefined = {
      total: total,
      current: currentPage,
      showTotal: (total) => (
        <Space.$ justify={Space.Justify.SpaceBetween}>
          <AddNewButton />
          <span>Total {total} items</span>
        </Space.$>
      ),
      showSizeChanger: true,
      defaultPageSize,
      pageSizeOptions: [defaultPageSize, 10, 20, 50, 100],
      onChange: (page, recordsPerPage) => setSearchParams({ page, recordsPerPage }),
      className: styles.messageList__footer,
    };

    return (
      <Table
        pagination={paginationProps}
        rowKey={'ID'}
        loading={isLoading}
        columns={columns}
        dataSource={cleanedMessages}
      />
    );
  };
}
